import { Space, Typography } from 'antd'
import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { StoreState } from 'src/store/configureStore'
import { ExplainableAIResultProps } from './ExplainableAIBarChart'
import { removeUnderScore } from './utils'
import useTranslate from 'src/utils/useTranslate'
import { useValue } from 'src/utils/useValue'
import Plot from 'react-plotly.js'

const { Title } = Typography

export const ExplainableAIBeeswarmPlotly = ({ property, model, filterBy, property_type, propertyClass, property_name }: ExplainableAIResultProps) => {
  const { explainableAIData } = useSelector((state: StoreState) => state.explainableAI)

  const isClassifier = property_type === 'CLASSIFIER'

  const chartData = useMemo(() => {
    const data = model?.map((modelItem: any) => ({
      modelName: modelItem,
      beeswarm: isClassifier
        ? explainableAIData?.[property]?.[modelItem]?.plots?.beeswarm?.[propertyClass]
        : explainableAIData?.[property]?.[modelItem]?.plots?.beeswarm,
      exps: explainableAIData?.[property]?.formulation_display_id || []
    }))
    return data
  }, [model, property, explainableAIData, propertyClass, isClassifier])

  const paramsToShow = useMemo(() => {
    if (filterBy.length === 0) return explainableAIData?.[property]?.x_cols
    else return filterBy
  }, [explainableAIData, filterBy, property])

  const filteredBeeswarmData = useMemo(() => {
    const output: any[] = []

    chartData.forEach((data: any, index: number) => {
      output[index] = {
        modelName: data.modelName,
        beeswarm: data.beeswarm,
        exps: data.exps
      }
    })

    return output
  }, [chartData])

  return (
    <Space
      style={{
        display: 'flex',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: 10
      }}
      styles={{
        item: {
          height: '100%',
          width: '100%'
        }
      }}
    >
      {filteredBeeswarmData?.map((data: any) => (
        <div
          style={{
            border: '1px solid #ccc',
            padding: 12,
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
            height: '100%',
            width: '100%'
          }}
          key={data.modelName}
        >
          <Title level={4} style={{ textAlign: 'center' }}>
            {removeUnderScore(data.modelName) ?? data.modelName}
          </Title>
          <SwarmPlot data={data.beeswarm} paramsToShow={paramsToShow} property={property} exps={data.exps} propertyName={property_name} />
        </div>
      ))}
    </Space>
  )
}

const SwarmPlot = ({ data, property, paramsToShow, propertyName, exps }: any) => {
  const [t] = useTranslate()
  const { getValue } = useValue()

  const displayNames = useSelector((state: StoreState) => state.displayNames.data)

  const currentPropertyName = useMemo(() => {
    return propertyName ?? displayNames.properties?.[property]?.name ?? property
  }, [displayNames, property, propertyName])

  const beeswarmData = useMemo(() => data.custom_data_beeswarm, [data.custom_data_beeswarm])

  const sortedParams = useMemo(() => {
    return data.feature_order
  }, [data.feature_order])

  const yCategories = useMemo(() => {
    return sortedParams?.filter((param: any) => paramsToShow.includes(param))?.map((param: any, idx: number) => sortedParams.indexOf(param))
  }, [paramsToShow, sortedParams])

  const yCategoriesNames = useMemo(() => {
    return sortedParams?.filter((param: any) => paramsToShow.includes(param))
  }, [paramsToShow, sortedParams])

  const getData = useCallback(
    (param: number) => {
      const finalPos = yCategoriesNames.indexOf(param)
      const currentPos = yCategories[finalPos]

      const offset = currentPos - finalPos
      const x = beeswarmData[param].x_coords
      const y = beeswarmData[param].y_coords.map((y: any) => y - offset)

      const color = beeswarmData[param].colors
      return x.map((_: any, idx: number) => {
        return {
          x: x[idx],
          y: y[idx],
          color: color[idx],
          exps: exps[idx]
        }
      })
    },
    [beeswarmData, yCategories, yCategoriesNames, exps]
  )

  const seriesData = useMemo(() => {
    return Object.keys(beeswarmData)
      ?.filter((param: any) => paramsToShow.includes(param))
      ?.map((param: any) => ({
        name: param,
        data: getData(param)
      }))
  }, [beeswarmData, getData, paramsToShow])

  const xCoordinates = useMemo(() => {
    return seriesData.map((series: any) => series.data.map((d: any) => d.x)).flat()
  }, [seriesData])

  const yCoordinates = useMemo(() => {
    return seriesData.map((series: any) => series.data.map((d: any) => d.y)).flat()
  }, [seriesData])

  const pointColors = useMemo(() => {
    return seriesData.map((series: any) => series.data.map((d: any) => d.color)).flat()
  }, [seriesData])

  const pointTooltips = useMemo(() => {
    return seriesData
      .map((series: any, idx: number) =>
        series.data.map((d: any) => {
          return `
        <b>${yCategoriesNames[idx]}</b>
        <br>
        <b>${t('common.experiment')}: ${d.exps}</b>
        <br>
        <b>X: ${getValue(d.y)}</b>
        <br>
        <b>Y: ${getValue(d.x)}</b>
        `
        })
      )
      .flat()
  }, [seriesData, t, getValue, yCategoriesNames])

  return (
    <div
      style={{
        width: '100%',
        display: 'flex',
        gap: '1rem'
      }}
    >
      <div
        style={{
          maxHeight: '600px',
          overflowY: 'auto',
          overflowX: 'hidden',
          width: 'calc(100% - 5rem)'
        }}
      >
        <BeeswarmPlot
          xCoordinates={xCoordinates}
          yCoordinates={yCoordinates}
          pointTooltips={pointTooltips}
          pointColors={pointColors}
          xAxisLabel={`${t('common.impactOn')} ${currentPropertyName}`}
          yAxisTicks={yCategoriesNames}
        />
      </div>
      <ColorScale params={paramsToShow.length} />
    </div>
  )
}

const BeeswarmPlot = ({
  xCoordinates = [],
  yCoordinates = [],
  pointTooltips = [],
  pointColors = [],
  yAxisTicks = [],
  xAxisLabel = 'X Axis'
}: {
  xCoordinates: number[]
  yCoordinates: number[]
  pointTooltips: string[]
  pointColors: string[]
  yAxisTicks: string[]
  xAxisLabel?: string
}) => {
  return (
    <Plot
      data={[
        {
          type: 'scattergl',
          mode: 'markers',
          x: xCoordinates,
          y: yCoordinates,
          marker: {
            size: 6,
            color: pointColors
          },
          text: pointTooltips,
          hoverinfo: 'text'
        }
      ]}
      layout={{
        height: yAxisTicks.length > 11 ? yAxisTicks.length * 50 : 550,
        dragmode: false,
        hovermode: 'closest',
        hoverlabel: {
          bordercolor: 'black',
          bgcolor: 'black',
          font: {
            color: 'white',
            size: 12,
            family: 'Arial'
          }
        },
        xaxis: {
          title: {
            text: xAxisLabel
          },
          showgrid: false,
          zeroline: true,

          ticks: 'outside',
          tickcolor: 'black',
          ticklen: 5,
          tickwidth: 1,

          showline: true,
          linecolor: 'black',
          linewidth: 1,

          automargin: true,
          autorange: true
        },
        yaxis: {
          showgrid: false,
          zeroline: false,

          ticks: 'outside',
          tickcolor: 'black',
          ticklen: 5,
          tickwidth: 1,

          showline: true,
          linecolor: 'black',
          linewidth: 1,

          categoryarray: Array.from({ length: yAxisTicks.length }, (_, i) => i),
          categoryorder: 'array',
          tickvals: Array.from({ length: yAxisTicks.length }, (_, i) => i),
          ticktext: yAxisTicks,
          tickmode: 'array',
          tickfont: {
            size: 10,
            family: 'Arial'
          },
          automargin: true,
          autorange: true
        }
      }}
      config={{
        displayModeBar: false,
        scrollZoom: false,
      }}
      style={{ width: '100%', height: '100%' }}
    />
  )
}

const ColorScale = ({ params }: any) => {
  const [t] = useTranslate()

  return (
    <div
      style={{
        position: 'relative',
        height: 'calc(600px - 50px)'
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 24,
          fontSize: '0.75rem'
        }}
      >
        {t('common.high')}
      </div>
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 24,
          fontSize: '0.75rem'
        }}
      >
        {t('common.low')}
      </div>
      <div
        style={{
          position: 'absolute',
          top: '50%',
          rotate: '270deg',
          left: -70,
          fontSize: '0.625rem',
          width: 200,
          textAlign: 'center',
          transform: 'translateX(10px)'
        }}
      >
        {`${t('common.ingredientQuantity')} / ${t('common.processParameter')}`}
      </div>
      <div
        style={{
          height: '100%',
          width: '12px',
          background: 'linear-gradient(to bottom, #fe0150, #008eff)'
        }}
      ></div>
    </div>
  )
}
